<template lang="pug">
main.p-trend
  .p-trend__container
    .p-trend__info-area
      .p-trend__info
        p KURAMA
        p {{ kuramaId }}
      .p-trend__info
        p メッシュコード
        p {{ meshCode }}
      .p-trend__info
        p 期間
        p {{ from }} 〜 {{ to }}
    .p-trend__controls(v-if='hasData')
      .p-trend__control.p-trend__control--slider
        span.p-trend__label Y軸:
        el-slider.p-trend__slider(
          v-model='sliderModel'
          range
          :min='sliderTrack.min'
          :max='sliderTrack.max'
          :step='sliderTrack.step'
          :format-tooltip='formatSliderTooltip'
          @change='onSliderChange'
        )
      .p-trend__control
        input.p-trend__num(
          type='number' step='any' placeholder='min'
          :class='{ "p-trend__num--invalid": minInvalid }'
          v-model='yMinStr' @blur='onInputBlur'
        )
        span 〜
        input.p-trend__num(
          type='number' step='any' placeholder='max'
          :class='{ "p-trend__num--invalid": maxInvalid }'
          v-model='yMaxStr' @blur='onInputBlur'
        )
      el-radio-group.p-trend__control(v-model='yLog' size='mini')
        el-radio-button(:label='false') 線形
        el-radio-button(:label='true') 対数
      .p-trend__control.p-trend__control--slider
        span.p-trend__label X軸:
        el-slider.p-trend__slider(
          v-model='xSliderModel'
          range
          :min='xSliderTrack.min'
          :max='xSliderTrack.max'
          :step='xSliderTrack.step'
          :format-tooltip='formatXSliderTooltip'
        )
      .p-trend__control
        input.p-trend__num.p-trend__num--datetime(
          type='datetime-local'
          :class='{ "p-trend__num--invalid": xMinInvalid }'
          v-model='xMinStr' @blur='onInputBlur'
        )
        span 〜
        input.p-trend__num.p-trend__num--datetime(
          type='datetime-local'
          :class='{ "p-trend__num--invalid": xMaxInvalid }'
          v-model='xMaxStr' @blur='onInputBlur'
        )
      button.p-trend__reset(type='button' @click='resetScales') リセット
    .p-trend__drawing(ref='drawingRef' v-loading='state === "loading"')
      template(v-if='state === "error"')
        .p-trend__msg
          p データの取得に失敗しました。
          el-button(@click='fetchData') 再試行
      template(v-else-if='state === "empty"')
        .p-trend__msg 期間内にデータがありません。
      template(v-else-if='state === "ready"')
        TrendChart(
          :data='chartData'
          :width='chartWidth'
          :height='chartHeight'
          :y-min='yMin'
          :y-max='yMax'
          :x-min='xMin'
          :x-max='xMax'
          :y-log='yLog'
        )
</template>

<script>
import * as apiTrend from '@/api/trend';
import TrendChart from '@/components/Element/TrendChart';

export default {
  name: 'PagesTrend',
  components: { TrendChart },
  data() {
    return {
      kuramaId: '',
      meshCode: '',
      from: '',
      to: '',
      state: 'loading',
      chartData: [],
      chartWidth: 800,
      chartHeight: 400,
      yMinStr: '',
      yMaxStr: '',
      yLog: false,
      xMinStr: '',
      xMaxStr: '',
    };
  },
  computed: {
    hasData() {
      return this.state === 'ready' && this.chartData.length > 0;
    },
    yMin() {
      if (this.yMinStr === '' || this.yMinStr == null) return null;
      const n = Number(this.yMinStr);
      return isNaN(n) ? null : n;
    },
    yMax() {
      if (this.yMaxStr === '' || this.yMaxStr == null) return null;
      const n = Number(this.yMaxStr);
      return isNaN(n) ? null : n;
    },
    minInvalid() {
      if (this.yMinStr === '' || this.yMinStr == null) return false;
      const n = Number(this.yMinStr);
      if (isNaN(n)) return true;
      if (this.yLog && n <= 0) return true;
      return false;
    },
    maxInvalid() {
      if (this.yMaxStr === '' || this.yMaxStr == null) return false;
      const n = Number(this.yMaxStr);
      if (isNaN(n)) return true;
      if (this.yLog && n <= 0) return true;
      return false;
    },
    dataRange() {
      let dMin = Infinity;
      let dMax = -Infinity;
      let posMin = Infinity;
      for (let i = 0; i < this.chartData.length; i += 1) {
        const v = this.chartData[i][1];
        if (v == null || isNaN(v)) continue;
        if (v < dMin) dMin = v;
        if (v > dMax) dMax = v;
        if (v > 0 && v < posMin) posMin = v;
      }
      if (!isFinite(dMin)) {
        return { min: 0, max: 1, posMin: 0.001 };
      }
      if (!isFinite(posMin)) posMin = Math.max(dMin, 0.001);
      return { min: dMin, max: dMax, posMin };
    },
    sliderTrack() {
      const { max, posMin } = this.dataRange;
      if (this.yLog) {
        const lo = Math.log10(posMin);
        const hi = Math.log10(Math.max(max, posMin * 10));
        const span = hi - lo || 1;
        return {
          min: Number((lo - span * 0.5).toFixed(4)),
          max: Number((hi + span * 0.5).toFixed(4)),
          step: 0.01,
        };
      }
      const hi = max > 0 ? max * 1.5 : 1;
      return {
        min: 0,
        max: Number(hi.toPrecision(3)),
        step: Number((hi / 200).toPrecision(2)) || 0.001,
      };
    },
    sliderModel: {
      get() {
        const { min: trackMin, max: trackMax } = this.sliderTrack;
        const fallbackMin = this.yLog ? Math.log10(this.dataRange.posMin) : 0;
        const fallbackMax = this.yLog
          ? Math.log10(Math.max(this.dataRange.max, this.dataRange.posMin))
          : this.dataRange.max;
        let lo =
          this.yMin != null
            ? this.yLog
              ? Math.log10(Math.max(this.yMin, this.dataRange.posMin))
              : this.yMin
            : fallbackMin;
        let hi =
          this.yMax != null
            ? this.yLog
              ? Math.log10(Math.max(this.yMax, this.dataRange.posMin))
              : this.yMax
            : fallbackMax;
        if (lo < trackMin) lo = trackMin;
        if (hi > trackMax) hi = trackMax;
        if (lo > hi) [lo, hi] = [hi, lo];
        return [lo, hi];
      },
      set([lo, hi]) {
        if (lo > hi) [lo, hi] = [hi, lo];
        if (this.yLog) {
          this.yMinStr = String(Number(Math.pow(10, lo).toPrecision(4)));
          this.yMaxStr = String(Number(Math.pow(10, hi).toPrecision(4)));
        } else {
          this.yMinStr = String(Number(lo.toPrecision(4)));
          this.yMaxStr = String(Number(hi.toPrecision(4)));
        }
      },
    },
    xMin() {
      if (this.xMinStr === '' || this.xMinStr == null) return null;
      const ms = new Date(this.xMinStr).getTime();
      if (isNaN(ms)) return null;
      return Math.floor(ms / 1000);
    },
    xMax() {
      if (this.xMaxStr === '' || this.xMaxStr == null) return null;
      const ms = new Date(this.xMaxStr).getTime();
      if (isNaN(ms)) return null;
      return Math.floor(ms / 1000);
    },
    xMinInvalid() {
      if (this.xMinStr === '' || this.xMinStr == null) return false;
      const ms = new Date(this.xMinStr).getTime();
      return isNaN(ms);
    },
    xMaxInvalid() {
      if (this.xMaxStr === '' || this.xMaxStr == null) return false;
      const ms = new Date(this.xMaxStr).getTime();
      return isNaN(ms);
    },
    xDataRange() {
      let dMin = Infinity;
      let dMax = -Infinity;
      for (let i = 0; i < this.chartData.length; i += 1) {
        const t = this.chartData[i][0];
        if (t == null || isNaN(t)) continue;
        if (t < dMin) dMin = t;
        if (t > dMax) dMax = t;
      }
      if (!isFinite(dMin)) {
        return { min: 0, max: 1 };
      }
      return { min: dMin, max: dMax };
    },
    xSliderTrack() {
      const { min, max } = this.xDataRange;
      const span = Math.max(1, max - min);
      return {
        min: Math.max(0, Math.floor(min - span * 0.5)),
        max: Math.ceil(max + span * 0.5),
        step: Math.max(1, Math.floor(span / 200)),
      };
    },
    xSliderModel: {
      get() {
        const { min: trackMin, max: trackMax } = this.xSliderTrack;
        const fallbackMin = this.xDataRange.min;
        const fallbackMax = this.xDataRange.max;
        let lo = this.xMin != null ? this.xMin : fallbackMin;
        let hi = this.xMax != null ? this.xMax : fallbackMax;
        if (lo < trackMin) lo = trackMin;
        if (hi > trackMax) hi = trackMax;
        if (lo > hi) [lo, hi] = [hi, lo];
        return [lo, hi];
      },
      set([lo, hi]) {
        if (lo > hi) [lo, hi] = [hi, lo];
        this.xMinStr = this.epochToDatetimeLocal(lo);
        this.xMaxStr = this.epochToDatetimeLocal(hi);
      },
    },
  },
  watch: {
    yLog(next, prev) {
      if (next === prev) return;
      if (next) {
        if (this.yMin != null && this.yMin <= 0) this.yMinStr = String(this.dataRange.posMin);
        if (this.yMax != null && this.yMax <= 0) this.yMaxStr = String(this.dataRange.max);
      }
    },
  },
  mounted() {
    const q = this.$route.query;
    this.kuramaId = q.kurama || '';
    this.meshCode = q.mesh || '';
    this.from = q.from || '';
    this.to = q.to || '';
    document.title = `KURAMA Trend - ${this.kuramaId}`;
    this.measureSize();
    window.addEventListener('resize', this.onResize);
    this.fetchData();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    onResize() {
      this.measureSize();
    },
    measureSize() {
      const host = this.$refs.drawingRef;
      const hostW = host ? host.clientWidth : window.innerWidth;
      this.chartWidth = Math.max(280, hostW);
      const isSmall = window.innerWidth < 600;
      const reserved = isSmall ? 220 : 240;
      this.chartHeight = Math.max(240, window.innerHeight - reserved);
    },
    resetScales() {
      this.yMinStr = '';
      this.yMaxStr = '';
      this.xMinStr = '';
      this.xMaxStr = '';
      this.yLog = false;
    },
    onSliderChange() {
      // sliderModel setter already pushed values to yMinStr / yMaxStr
    },
    onInputBlur() {
      const yLo = this.yMin;
      const yHi = this.yMax;
      if (yLo != null && yHi != null && yLo > yHi) {
        this.yMinStr = String(yHi);
        this.yMaxStr = String(yLo);
      }
      const xLo = this.xMin;
      const xHi = this.xMax;
      if (xLo != null && xHi != null && xLo > xHi) {
        const tmp = this.xMinStr;
        this.xMinStr = this.xMaxStr;
        this.xMaxStr = tmp;
      }
    },
    formatSliderTooltip(v) {
      if (this.yLog) {
        return `${Math.pow(10, v).toPrecision(3)} μSv/h`;
      }
      return `${Number(v.toPrecision(3))} μSv/h`;
    },
    formatXSliderTooltip(v) {
      return this.formatDateTime(v);
    },
    epochToDatetimeLocal(epochSec) {
      const d = new Date(epochSec * 1000);
      const p = (n) => String(n).padStart(2, '0');
      return (
        `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}` +
        `T${p(d.getHours())}:${p(d.getMinutes())}`
      );
    },
    formatDateTime(epochSec) {
      const d = new Date(epochSec * 1000);
      const p = (n) => String(n).padStart(2, '0');
      return (
        `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ` +
        `${p(d.getHours())}:${p(d.getMinutes())}`
      );
    },
    fetchData() {
      this.state = 'loading';
      apiTrend
        .get({ kurama: this.kuramaId, mesh: this.meshCode, from: this.from, to: this.to })
        .then((res) => {
          const data = (res.data && res.data.data) || [];
          if (data.length === 0) {
            this.state = 'empty';
            this.chartData = [];
          } else {
            this.chartData = data;
            this.state = 'ready';
          }
        })
        .catch(() => {
          this.state = 'error';
          this.chartData = [];
        });
    },
  },
};
</script>

<style lang="postcss" scoped>
.p-trend
  padding: 20px
  box-sizing: border-box
  @media (max-width: 600px)
    padding: 12px
.p-trend__container
  max-width: 100%
.p-trend__info-area
  display: flex
  flex-wrap: wrap
  gap: 8px 24px
  margin-bottom: 16px
  font-size: 13px
  @media (max-width: 600px)
    gap: 4px 16px
    font-size: 12px
    margin-bottom: 12px
.p-trend__info
  display: flex
  gap: 8px
  min-width: 0
  p
    margin: 0
    word-break: break-all
  p:first-child
    color: #888
    flex-shrink: 0
  p:last-child
    font-weight: bold
.p-trend__controls
  display: flex
  flex-wrap: wrap
  gap: 8px 16px
  align-items: center
  margin-bottom: 12px
  font-size: 13px
  @media (max-width: 600px)
    gap: 6px 12px
    font-size: 12px
.p-trend__control
  display: inline-flex
  align-items: center
  gap: 6px
.p-trend__control--slider
  flex: 1 1 240px
  min-width: 220px
.p-trend__label
  color: #4a5568
  flex-shrink: 0
.p-trend__slider
  flex: 1
  margin: 0 8px
.p-trend__num
  width: 90px
  padding: 2px 6px
  border: 1px solid #d0d4db
  border-radius: 3px
  font-size: 12px
  font-variant-numeric: tabular-nums
.p-trend__num--datetime
  width: 160px
  font-size: 11px
.p-trend__num--invalid
  border-color: #f56c6c
  background: #fef0f0
.p-trend__reset
  padding: 2px 10px
  border: 1px solid #d0d4db
  border-radius: 3px
  background: #fff
  cursor: pointer
  font-size: 12px
  &:hover
    background: #f5f7fa
.p-trend__drawing
  min-height: 240px
.p-trend__msg
  display: flex
  flex-direction: column
  align-items: center
  justify-content: center
  gap: 16px
  height: 300px
</style>
